/**
 * Bảng chỉnh ngưỡng đối chiếu (`auditRec`) — US D1, PRD mục 6 câu 5.
 *
 * Vì sao nằm trong Question Builder chứ không phải một màn hình riêng: ngưỡng là
 * **dữ liệu của phiên bản bộ câu hỏi** (`form_versions.audit_rules_snapshot`), đóng
 * băng theo R6. Sửa ngưỡng = tạo bản nháp mới, publish mới có hiệu lực — và phiếu
 * cũ chạy lại audit vẫn ra đúng kết quả cũ. Tách thành màn hình riêng sẽ gợi ý rằng
 * chúng sống độc lập với bộ câu hỏi, mà chúng thì không.
 *
 * ⚠️ Khác hẳn màn hình "Mục đối chiếu" (`#/audit-rules`): ở đó là BẬT/TẮT từng luật,
 * một thao tác vận hành áp dụng ngay. Ở đây là NGƯỠNG, đóng băng theo phiên bản.
 * Hai thứ có vòng đời khác nhau — xem `AuditRuleSettingsService` phía API.
 *
 * Bảng chỉ liệt kê những chỉ số mà bộ luật của **bộ câu hỏi đang mở** thật sự đọc
 * tới (`auditRecKeysFor`): luật A dùng 37 chỉ số, luật B dùng 4, không chỉ số nào
 * dùng chung. Trước đây cả 41 chỉ số hiện ở cả hai bộ — admin mở "Đo đạc trên vườn"
 * rồi sửa `fertInorgHa` ở đó, mà không luật nào của bộ đó đọc tới nên chẳng có gì
 * đổi. Snapshot vẫn được lưu **nguyên vẹn**: chỉ số của bộ kia không hiện ra nhưng
 * cũng không bị xoá, vì `auditRec` là một object phẳng dùng chung cho cả hai bộ luật.
 *
 * Phạm vi: chỉ `auditRec` dạng ba dải. `fertMismatch`/`transportDiff` (hệ số lệch,
 * không phải dải) cùng `intercropSpecies`, `treeSpecs`, `yieldCurve`, `bandMaxAge`,
 * `fuelGroups` được mang nguyên vẹn qua khi lưu nhưng không sửa được ở đây — chúng
 * là bảng tra cứu nhiều chiều, cần một giao diện riêng và chưa ai yêu cầu sửa chúng.
 */
import { AUDIT_REC_FORM_KEY, AUDIT_REC_NON_BAND, auditRecKeysFor } from '@farmer/audit-rules';
import { esc } from '../ui/util.js';

/** Ba dải, theo đúng thứ tự bao nhau: rec ⊆ warn ⊆ err. */
const DAI = [
  ['rec', 'Khuyến nghị', 'Khoảng nông học mong đợi'],
  ['warn', 'Cảnh báo', 'Ngoài khoảng này thì gắn cờ vàng'],
  ['err', 'Lỗi', 'Ngoài khoảng này thì gắn cờ đỏ'],
];

const FORM_LABEL = { onfarm: 'Phỏng vấn nông hộ', removal: 'Đo đạc trên vườn' };

/**
 * `null` trong JSONB chính là `Infinity` lúc khai báo — JSON không có Infinity.
 * Ô trống trên giao diện nghĩa là "không giới hạn", đúng ngữ nghĩa đó.
 */
const raOO = (v) => (v === null || v === undefined || v === Infinity || v === -Infinity ? '' : String(v));
const vaoGiaTri = (s) => {
  const t = String(s).trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const duoi = (v) => (v === null || v === undefined ? -Infinity : Number(v));
const tren = (v) => (v === null || v === undefined ? Infinity : Number(v));

/**
 * Chia các chỉ số trong snapshot thành phần hiện được trên bảng của `formKey` và
 * phần không thuộc bộ luật nào.
 *
 * Chỉ số lạ (bảng tra không biết — snapshot của bộ luật cũ, hoặc chỉnh tay) vẫn được
 * liệt kê ở **cả hai** bộ: không có cách nào biết nó thuộc bộ nào, mà giấu đi thì
 * không còn đường nào sửa nó nữa.
 *
 * @param {object} auditRec
 * @param {'onfarm'|'removal'} formKey
 * @returns {{ thuocBo: string[], la: string[] }}
 */
export function thresholdKeysFor(auditRec, formKey) {
  const co = Object.keys(auditRec || {});
  const thuocBo = auditRecKeysFor(formKey).filter(
    (k) => co.includes(k) && !AUDIT_REC_NON_BAND.includes(k),
  );
  const la = co.filter((k) => !AUDIT_REC_FORM_KEY[k]).sort();
  return { thuocBo, la };
}

/**
 * Cùng bộ luật mà máy chủ áp dụng (`FormVersionService.validateAuditRules`).
 * Kiểm ở client để người dùng thấy sai ngay khi gõ, chứ máy chủ mới là nơi quyết định.
 *
 * Quét **toàn bộ** `auditRec`, không riêng phần đang hiện: máy chủ cũng vậy, nên một
 * ngưỡng hỏng ở bộ kia vẫn chặn lưu phiên bản này và người dùng cần biết vì sao.
 */
export function lintThresholds(auditRec) {
  const loi = [];
  for (const [key, n] of Object.entries(auditRec || {})) {
    const dai = {};
    for (const [ten] of DAI) {
      const v = n?.[ten];
      if (!Array.isArray(v)) continue;
      const lo = duoi(v[0]);
      const hi = tren(v[1]);
      if (Number.isNaN(lo) || Number.isNaN(hi)) {
        loi.push({ key, field: ten, message: 'chứa giá trị không phải số' });
        continue;
      }
      if (lo > hi) loi.push({ key, field: ten, message: 'biên dưới lớn hơn biên trên' });
      dai[ten] = [lo, hi];
    }
    const long = (trongDai, ngoaiDai) => {
      const a = dai[trongDai];
      const b = dai[ngoaiDai];
      if (!a || !b) return;
      if (a[0] < b[0] || a[1] > b[1]) {
        loi.push({ key, field: trongDai, message: `phải nằm trong dải ${ngoaiDai}` });
      }
    };
    long('rec', 'warn');
    long('warn', 'err');
  }
  return loi;
}

/**
 * @param {HTMLElement} host
 * @param {object} auditRules - object `auditRules` đang soạn; hàm sửa TRỰC TIẾP lên nó
 * @param {'onfarm'|'removal'} formKey - bộ câu hỏi đang mở; quyết định bảng hiện chỉ số nào
 * @param {() => void} onChange - báo cho builder biết có thay đổi chưa lưu
 */
export function renderThresholdEditor(host, auditRules, formKey, onChange) {
  const auditRec = auditRules.auditRec || (auditRules.auditRec = {});
  const { thuocBo, la } = thresholdKeysFor(auditRec, formKey);
  const keys = [...thuocBo, ...la];
  const nhan = FORM_LABEL[formKey] || formKey;

  host.innerHTML = `
    <div class="card">
      <h3 class="card-title"><span class="ms">tune</span>Ngưỡng đối chiếu — ${esc(nhan)} (${keys.length} chỉ số)</h3>
      <p class="hint-text">
        Bảng chỉ liệt kê những chỉ số mà bộ luật của <b>${esc(nhan)}</b> đọc tới; ngưỡng của
        bộ câu hỏi kia vẫn được lưu nguyên vẹn, sửa ở màn hình của bộ đó.
        Ngưỡng thuộc về <b>phiên bản bộ câu hỏi</b>: lưu là ghi vào phiên bản đang mở, và
        phiếu đã tạo trước đó giữ nguyên ngưỡng của chúng. Để trống một ô nghĩa là
        <b>không giới hạn</b>. Ba dải phải bao nhau: khuyến nghị ⊆ cảnh báo ⊆ lỗi.
      </p>
      <div class="threshold-lint" id="thLint"></div>
      ${
        keys.length
          ? `<div class="table-scroll"><table class="list-table threshold-table">
              <thead><tr>
                <th>Chỉ số</th><th>Đơn vị</th>
                ${DAI.map(([, nhan, moTa]) => `<th title="${esc(moTa)}">${esc(nhan)}</th>`).join('')}
                <th>Ghi chú</th>
              </tr></thead>
              <tbody>${keys.map((k) => hangHtml(k, auditRec[k], la.includes(k))).join('')}</tbody>
            </table></div>`
          : `<p class="hint-text">Phiên bản này chưa có ngưỡng nào cho ${esc(nhan)}. Ngưỡng mặc định
               của <code>@farmer/audit-rules</code> sẽ được dùng.</p>`
      }
    </div>`;

  const lintHost = host.querySelector('#thLint');
  const hien = new Set(keys);

  function veLint() {
    const tatCa = lintThresholds(auditRec);
    // Ngưỡng hỏng ở bộ kia cũng chặn lưu (máy chủ quét cả object) nhưng không có ô nào
    // trên bảng này để tô đỏ — nên nói thẳng là phải sang bộ câu hỏi đó mà sửa, thay vì
    // để người dùng nhìn một bảng sạch lỗi rồi bấm lưu và nhận 422 không hiểu vì sao.
    const loi = tatCa.filter((l) => hien.has(l.key));
    const loiBoKia = tatCa.filter((l) => !hien.has(l.key));
    const doan = [];
    if (loi.length) {
      doan.push(`<div class="status-banner tone-error"><span class="ms">error</span>
           <span>${loi.length} ngưỡng không hợp lệ — máy chủ sẽ từ chối lưu:
           ${loi.slice(0, 3).map((l) => `<b>${esc(l.key)}.${esc(l.field)}</b> ${esc(l.message)}`).join('; ')}
           ${loi.length > 3 ? `… và ${loi.length - 3} lỗi nữa` : ''}</span></div>`);
    }
    if (loiBoKia.length) {
      const boKia = formKey === 'onfarm' ? FORM_LABEL.removal : FORM_LABEL.onfarm;
      doan.push(`<div class="status-banner tone-error"><span class="ms">error</span>
           <span>${loiBoKia.length} ngưỡng của <b>${esc(boKia)}</b> không hợp lệ và cũng chặn lưu
           phiên bản này (${loiBoKia.slice(0, 3).map((l) => `<b>${esc(l.key)}.${esc(l.field)}</b>`).join(', ')}${loiBoKia.length > 3 ? '…' : ''}).
           Chuyển sang bộ câu hỏi đó để sửa.</span></div>`);
    }
    lintHost.innerHTML = doan.join('');
    // Đánh dấu đúng ô sai để không phải dò trong 37 hàng.
    host.querySelectorAll('.threshold-input').forEach((i) => i.classList.remove('invalid'));
    for (const l of loi) {
      host
        .querySelectorAll(`[data-key="${CSS.escape(l.key)}"][data-dai="${l.field}"]`)
        .forEach((i) => i.classList.add('invalid'));
    }
  }

  host.querySelectorAll('.threshold-input').forEach((input) =>
    input.addEventListener('input', () => {
      const { key, dai, bien } = input.dataset;
      const nguong = auditRec[key];
      if (!Array.isArray(nguong[dai])) nguong[dai] = [null, null];
      nguong[dai][bien === 'lo' ? 0 : 1] = vaoGiaTri(input.value);
      veLint();
      onChange();
    }),
  );

  veLint();
}

function hangHtml(key, n, laLa) {
  const oSo = (dai, bien, v) =>
    `<input class="threshold-input" inputmode="decimal" data-key="${esc(key)}"
       data-dai="${dai}" data-bien="${bien}" value="${esc(raOO(v))}"
       placeholder="∞" aria-label="${esc(key)} ${dai} ${bien === 'lo' ? 'biên dưới' : 'biên trên'}">`;

  return `<tr>
    <td><code>${esc(key)}</code>${laLa ? ' <span class="hint-text">(không thuộc bộ luật nào)</span>' : ''}</td>
    <td class="hint-text">${esc(n?.u || '')}</td>
    ${DAI.map(([ten]) => {
      const v = Array.isArray(n?.[ten]) ? n[ten] : [null, null];
      return `<td><div class="threshold-pair">${oSo(ten, 'lo', v[0])}<span>–</span>${oSo(ten, 'hi', v[1])}</div></td>`;
    }).join('')}
    <td class="hint-text">${esc(n?.note || '')}</td>
  </tr>`;
}
